import React from "react";
import { observer } from "mobx-react-lite";
import {
  Box,
  FormControlLabel,
  Slider,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { AnalysisService } from "../../services/analysis/AnalysisService";
import { ConfidenceOptions } from "../../pages/analysis/components/video-analysis-diagram/tools/Tools";
import { translationService } from "../../services/translation/TranslationService";

export interface AnalysisDisplayOptions {
  showCharacterizations: boolean;
  mergeNearRegions: boolean;
  mergeDistance: number; // metres (or seconds when the analysis is time-based)
}

interface AnalysisControlsProps {
  videoAnalysis: AnalysisService;
  options: AnalysisDisplayOptions;
  onChange: (next: AnalysisDisplayOptions) => void;
  // The unit the merge threshold is expressed in for the current data.
  mergeUnit?: string;
  /**
   * Hide the confidence mode/slider. Set when the rows on screen are the reviewer's approved
   * list rather than model output — a confidence threshold cannot filter a signed-off list,
   * so showing the slider would imply an effect it does not have.
   */
  hideConfidence?: boolean;
}

const t = (key: string) => translationService.translate(key);

/**
 * Shared "Refinement / Comparison / Overall Confidence" controls used on both
 * the Video Analysis and Comparison tabs. Overall Confidence is backed by the
 * AnalysisService (so it drives predictions everywhere); Show Characterizations
 * and Merge Near Regions are view options owned by the parent.
 */
const AnalysisControls: React.FC<AnalysisControlsProps> = observer(
  ({ videoAnalysis, options, onChange, mergeUnit = "m", hideConfidence = false }) => {
    const confidencePct = Math.round(videoAnalysis.analysisOverallConfidence * 100);
    const isOverall =
      videoAnalysis.analysisConfidenceOption === ConfidenceOptions.Overall;

    const setConfidence = (value: number) => {
      videoAnalysis.setAnalysisConfidenceOption(ConfidenceOptions.Overall);
      videoAnalysis.setAnalysisOverallConfidence(value / 100);
    };

    return (
      // data-html2canvas-ignore: every control in this row is an instrument for reading the
      // analysis on screen -- a confidence threshold, a characterization toggle, a merge switch.
      // None of it is part of the inspection, so none of it belongs in the exported report; the
      // row sits inside the graph the PDF captures, which is the only reason it appeared there.
      <Box
        data-html2canvas-ignore
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 3,
          px: 2,
          py: 1,
          mb: 2,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
        }}
      >
        {/* Confidence mode: Recommended (per-code thresholds) vs Overall (one slider) */}
        {!hideConfidence && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography variant="body2">{t("Analysis.confidence")}</Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={isOverall ? "overall" : "recommended"}
            onChange={(_e, v) => {
              if (v === "recommended")
                videoAnalysis.setAnalysisConfidenceOption(ConfidenceOptions.Recommended);
              else if (v === "overall")
                videoAnalysis.setAnalysisConfidenceOption(ConfidenceOptions.Overall);
            }}
          >
            <ToggleButton value="recommended">
              {t("Analysis.confidence-option.recommended")}
            </ToggleButton>
            <ToggleButton value="overall">
              {t("Analysis.confidence-option.overall")}
            </ToggleButton>
          </ToggleButtonGroup>
          {isOverall && (
            <Box sx={{ minWidth: 150 }}>
              <Typography variant="caption" sx={{ display: "block", lineHeight: 1.2 }}>
                {t("Analysis.confidence-option.overall")}: <b>{confidencePct}%</b>
              </Typography>
              <Slider
                value={confidencePct}
                min={65}
                max={100}
                step={1}
                size="small"
                valueLabelDisplay="auto"
                sx={{ py: 0.5 }}
                onChange={(_e, v) => setConfidence(v as number)}
              />
            </Box>
          )}
        </Box>
        )}

        {/* Comparison: show characterizations */}
        <FormControlLabel
          control={
            <Switch
              checked={options.showCharacterizations}
              onChange={(e) =>
                onChange({ ...options, showCharacterizations: e.target.checked })
              }
            />
          }
          label={t("comparison.showCharacterizations")}
        />

        {/* Refinement: merge near regions */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={options.mergeNearRegions}
                onChange={(e) =>
                  onChange({ ...options, mergeNearRegions: e.target.checked })
                }
              />
            }
            label={t("comparison.mergeNearRegions")}
          />
          {options.mergeNearRegions && (
            <Box sx={{ minWidth: 150 }}>
              <Typography variant="caption" sx={{ display: "block", lineHeight: 1.2 }}>
                {t("comparison.mergeDistance")}:{" "}
                <b>
                  {options.mergeDistance.toFixed(2)} {mergeUnit}
                </b>
              </Typography>
              <Slider
                value={options.mergeDistance}
                min={0}
                max={mergeUnit === "m" ? 2 : 10}
                step={mergeUnit === "m" ? 0.1 : 1}
                size="small"
                valueLabelDisplay="auto"
                sx={{ py: 0.5 }}
                onChange={(_e, v) =>
                  onChange({ ...options, mergeDistance: v as number })
                }
              />
            </Box>
          )}
        </Box>
      </Box>
    );
  }
);

export const defaultAnalysisDisplayOptions: AnalysisDisplayOptions = {
  showCharacterizations: false,
  mergeNearRegions: false,
  mergeDistance: 0.7,
};

export default AnalysisControls;
